<template>
  <div
    class="coverage-file-item cursor-pointer border border-gray-200 rounded-lg p-3 transition-all duration-200 hover:border-blue-300 hover:shadow-md"
    :style="{ animationDelay: `${index * 100}ms` }"
    @click="emit('viewDetail', fileDetail)"
  >
    <div class="mb-2 flex items-center justify-between">
      <div class="flex flex-1 items-center min-w-0 space-x-2">
        <file-outlined :class="getFileIconClass()" />
        <span class="truncate text-sm font-medium text-gray-800" :title="fileDetail.file_path">
          {{ fileName }}
        </span>
        <a-badge v-if="hasRiskGaps" :count="riskGapsCount" :number-style="{ backgroundColor: getRiskBadgeColor() }">
          <warning-outlined :class="getRiskIconClass()" />
        </a-badge>
      </div>
      <div class="flex flex-shrink-0 items-center space-x-2">
        <span class="text-sm font-bold" :class="getCoverageTextClass()">{{ coveragePercentage }}%</span>
        <right-outlined class="text-gray-400" />
      </div>
    </div>

    <div class="mb-2 h-1.5 w-full rounded-full bg-gray-200">
      <div
        class="h-1.5 rounded-full transition-all duration-1000"
        :class="getCoverageBarClass()"
        :style="{ width: `${coveragePercentage}%` }"
      ></div>
    </div>

    <div class="flex items-center justify-between text-xs text-gray-500">
      <div class="flex items-center space-x-3">
        <div class="flex items-center space-x-1">
          <check-circle-outlined class="text-green-500" />
          <span>已覆盖</span>
          <span class="font-medium text-green-600">{{ coveredFunctionsCount }} 个函数</span>
        </div>
        <div class="flex items-center space-x-1">
          <exclamation-circle-outlined class="text-red-500" />
          <span>未覆盖</span>
          <span class="font-medium text-red-600">{{ uncoveredFunctionsCount }} 个函数</span>
        </div>
      </div>
      <div v-if="hasRiskGaps" class="flex items-center space-x-1">
        <span :class="getRiskTextClass()">{{ getHighestRiskLevel() }}风险</span>
      </div>
    </div>

    <!-- 风险提示 -->
    <div v-if="hasHighRiskGaps" class="mt-2 rounded p-2" :class="getRiskAlertClass()">
      <div class="flex items-center text-xs">
        <warning-outlined :class="getRiskIconClass()" class="mr-1" />
        <span class="font-medium">{{ getHighestRiskLevel() }}风险：</span>
        <span class="ml-1">{{ getHighestRiskReason() }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CoverageGap, FileCoverageDetail } from '@/types/precision-new/ai-analysis';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons-vue';
import { computed } from 'vue';

// Props
interface Props {
  fileDetail: FileCoverageDetail;
  coverageGaps?: CoverageGap[];
  index: number;
}

const props = withDefaults(defineProps<Props>(), {
  coverageGaps: () => [],
});

// Emits
const emit = defineEmits(['viewDetail']);

// Computed
const fileName = computed(() => props.fileDetail.file_path.split('/').pop() || props.fileDetail.file_path);

const coveragePercentage = computed(() => Math.round((props.fileDetail.estimated_coverage || 0) * 100));

const coveredFunctionsCount = computed(() => props.fileDetail.covered_functions?.length || 0);

const uncoveredFunctionsCount = computed(() => props.fileDetail.uncovered_functions?.length || 0);

const hasRiskGaps = computed(() => props.coverageGaps && props.coverageGaps.length > 0);

const hasHighRiskGaps = computed(() => {
  return props.coverageGaps?.some((gap) => gap.risk_level === 'high' || gap.risk_level === 'critical') || false;
});

const riskGapsCount = computed(() => props.coverageGaps?.length || 0);

const highestRiskGap = computed(() => {
  if (!props.coverageGaps || props.coverageGaps.length === 0) {
    return null;
  }

  const riskLevels = ['critical', 'high', 'medium', 'low'];
  for (const level of riskLevels) {
    const gap = props.coverageGaps.find((g) => g.risk_level === level);
    if (gap) {
      return gap;
    }
  }
  return props.coverageGaps[0];
});

// 方法
function getFileIconClass() {
  const percentage = coveragePercentage.value;
  if (percentage >= 80) {
    return 'text-green-500';
  }
  if (percentage >= 60) {
    return 'text-yellow-500';
  }
  return 'text-red-500';
}

function getCoverageTextClass() {
  const percentage = coveragePercentage.value;
  if (percentage >= 80) {
    return 'text-green-600';
  }
  if (percentage >= 60) {
    return 'text-yellow-600';
  }
  return 'text-red-500';
}

function getCoverageBarClass() {
  const percentage = coveragePercentage.value;
  if (percentage >= 80) {
    return 'bg-green-500';
  }
  if (percentage >= 60) {
    return 'bg-yellow-500';
  }
  return 'bg-red-500';
}

function getRiskBadgeColor() {
  if (!highestRiskGap.value) {
    return '#f87171';
  }
  const riskColors = {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#ca8a04',
    low: '#16a34a',
  };
  return riskColors[highestRiskGap.value.risk_level as keyof typeof riskColors] || '#f87171';
}

function getRiskIconClass() {
  if (!highestRiskGap.value) {
    return 'text-red-500';
  }
  const riskClasses = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-green-600',
  };
  return riskClasses[highestRiskGap.value.risk_level as keyof typeof riskClasses] || 'text-red-500';
}

function getRiskTextClass() {
  if (!highestRiskGap.value) {
    return 'text-red-600';
  }
  const riskClasses = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-green-600',
  };
  return riskClasses[highestRiskGap.value.risk_level as keyof typeof riskClasses] || 'text-red-600';
}

function getRiskAlertClass() {
  if (!highestRiskGap.value) {
    return 'bg-red-50 border border-red-200';
  }
  const riskClasses = {
    critical: 'bg-red-50 border border-red-200',
    high: 'bg-orange-50 border border-orange-200',
    medium: 'bg-yellow-50 border border-yellow-200',
    low: 'bg-green-50 border border-green-200',
  };
  return riskClasses[highestRiskGap.value.risk_level as keyof typeof riskClasses] || 'bg-red-50 border border-red-200';
}

function getHighestRiskLevel() {
  if (!highestRiskGap.value) {
    return '';
  }
  const riskTexts = {
    critical: '严重',
    high: '高',
    medium: '中',
    low: '低',
  };
  return riskTexts[highestRiskGap.value.risk_level as keyof typeof riskTexts] || '';
}

function getHighestRiskReason() {
  return highestRiskGap.value?.reason || '';
}
</script>

<style scoped>
.coverage-file-item {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
